import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useApp } from "@/hooks/use-app-context";
import { useConfirmedLogout } from "@/hooks/use-confirm";
import { saveAuthReturnPath } from "@/lib/auth-return";
import { useGeolocationConsentSettings } from "@/features/collection-place";
import type { AuthUser } from "@/types/auth.types";
import type { SettingsCopy } from "./settings-copy";
import { SettingsCard, SettingsRow, SettingsRows } from "./settings-ui";

interface AccountCardProps {
  user: AuthUser;
  copy: SettingsCopy;
  onPassword: () => void;
  onPrivacy: () => void;
}

/** Security, location consent, personal data, admin shortcut and sign-out. */
export function AccountCard({ user, copy, onPassword, onPrivacy }: AccountCardProps) {
  const { t, language, setUser } = useApp();
  const navigate = useNavigate();
  const geolocation = useGeolocationConsentSettings();
  const isAdmin = user.role === "admin" || user.role === "super_admin";

  const logout = useConfirmedLogout(
    useCallback(() => {
      saveAuthReturnPath();
      setUser(null);
      navigate(`/${language}/login`);
    }, [language, navigate, setUser]),
  );

  return (
    <SettingsCard id="settings-account" title={copy.accountTitle}>
      <SettingsRows>
        <SettingsRow icon="lock" label={copy.password} hint={copy.passwordHint} onClick={onPassword} />
        <SettingsRow
          icon="mapPin"
          label={t.geolocationConsentTitle}
          hint={
            <>
              <span className="block">{t.geolocationConsentDescription}</span>
              <span role="status" className="mt-1 block font-medium text-foreground">{geolocation.isGranted ? t.geolocationConsentGranted : t.geolocationConsentDenied}</span>
              {geolocation.hasError ? (
                <span role="alert" className="mt-1 block text-danger">
                  {t.geolocationConsentSaveError}
                </span>
              ) : null}
            </>
          }
        >
          <Button
            size="sm"
            variant={geolocation.isGranted ? "outline" : "default"}
            disabled={geolocation.isSaving}
            onClick={() => geolocation.updateConsent(geolocation.isGranted ? "denied" : "granted")}
          >
            {geolocation.isSaving
              ? t.savingGeolocationConsent
              : geolocation.isGranted
                ? t.withdrawGeolocationConsent
                : t.grantGeolocationConsent}
          </Button>
        </SettingsRow>
        <SettingsRow icon="shield" label={copy.myData} hint={copy.myDataHint} onClick={onPrivacy} />
        {isAdmin ? (
          <SettingsRow
            icon="layoutDashboard"
            label={copy.adminSpace}
            hint={t.settingsAdminAccessDescription}
            onClick={() => navigate(`/${language}/admin/dashboard`)}
          />
        ) : null}
        <SettingsRow icon="logOut" label={t.logout} hint={copy.logoutHint} onClick={() => void logout()} />
      </SettingsRows>
    </SettingsCard>
  );
}

/**
 * Account deletion stays apart from everyday settings, behind a dialog that
 * asks for the current password (DeleteAccountDialog).
 */
export function DeleteAccountCard({
  copy,
  onDelete,
  isDeleting,
}: {
  copy: SettingsCopy;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const { t } = useApp();
  return (
    <section aria-labelledby="settings-delete" className="rounded-lg border border-danger/30 bg-surface p-4 sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h2 id="settings-delete" className="text-sm font-semibold text-danger">
            {copy.dangerTitle}
          </h2>
          <p className="max-w-xl text-sm leading-6 text-muted-foreground">{t.deleteAccountDesc}</p>
        </div>
        <Button
          variant="outline"
          onClick={onDelete}
          disabled={isDeleting}
          className="shrink-0 border-danger/40 text-danger hover:bg-danger/10 hover:text-danger"
        >
          {isDeleting ? t.deleting : t.requestAccountDeletion}
        </Button>
      </div>
    </section>
  );
}
